import type { BlockType } from '@unifyapps/defs/types/block';
import type { DependsOnType } from '@unifyapps/defs/types/page';
import type BlocksRegistry from '../components/RegistryProvider/BlocksRegistry';
import { getIsEntitySuspendedDependencies } from '../components/DataSourceController/hooks/useIsEntitySuspended';
import { BlockHelper } from '../../helpers/BlockHelper';
import { ComputeContextKeys } from '../hooks/computeContext/const';
import { getDataSourceResponsesDeps } from '../hooks/useDataSourceResponses';
import { DependencyHelper } from '../../dependency/helpers/DependencyHelper';
import { ENTIRE_ENTITY } from './const';

function getDepsFromBlockImplicitDepsGroups({
  pageBlock,
  registry,
}: {
  pageBlock: BlockType;
  registry: BlocksRegistry;
}) {
  const dependencyGroups = registry.getBlockImplicitDependencyGroups(
    pageBlock.component.componentType,
  );

  const deps: DependsOnType[][] = [];

  if (dependencyGroups?.isEntitySuspended) {
    const suspendedGrpDeps = getIsEntitySuspendedDependencies(
      BlockHelper.getDataSourceIds(pageBlock),
    );
    deps.push(suspendedGrpDeps);
  }

  if (dependencyGroups?.usingUserContext) {
    deps.push([
      {
        id: ComputeContextKeys.USER_CONTEXT,
        p: [ENTIRE_ENTITY],
      },
    ]);
  }

  if (dependencyGroups?.usingDataSourcesResponse) {
    const dataSourcesResponse = getDataSourceResponsesDeps(BlockHelper.getDataSourceIds(pageBlock));
    deps.push(dataSourcesResponse);
  }

  return DependencyHelper.mergeDependsOn(deps);
}

export function buildBlockImplicitDependencies({
  block,
  registry,
}: {
  block: BlockType;
  registry: BlocksRegistry;
}) {
  // first get the implicit deps added by blocks,
  // these are things from the context that the block needs
  const blockImplicitDeps =
    registry.getBlockImplicitDependsOn(block.component.componentType, block) ?? [];

  // these deps are standard, since block must be using that hook/feature,
  // it has marked the associated dep group required
  const blockDepsFromImplicitDepsGroups = getDepsFromBlockImplicitDepsGroups({
    pageBlock: block,
    registry,
  });

  return DependencyHelper.mergeDependsOn([blockImplicitDeps, blockDepsFromImplicitDepsGroups]);
}
